/**
 * Chat-channel sound helpers.
 *
 * Two sounds are surfaced in the chat panel:
 *   1. A typing sound while a tool call is in flight (mirrors the
 *      voice channel, which uses typingSound during latency / function
 *      calls).
 *   2. A gentle "ding" when a fresh assistant message arrives.
 *
 * The typing sound delegates to the shared utils/typing-sound.ts module
 * so we don't duplicate the AudioContext + filter chain logic. The ding
 * is small enough to inline here.
 *
 * Mute scope = chat panel only and session only. Voice mode keeps using
 * typingSound directly and is unaffected by chatSounds.setMuted().
 */

import { typingSound } from './typing-sound';

type Profile = 'typing' | 'clicking';

let muted = false;
// AudioContext is created lazily — browsers gate it behind a user
// gesture, and the chat panel always opens via a click so by the time
// .ding() runs, the gesture requirement is satisfied.
let dingCtx: AudioContext | null = null;

function getDingCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (dingCtx) return dingCtx;
  try {
    const Ctor =
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    dingCtx = new Ctor();
    return dingCtx;
  } catch {
    return null;
  }
}

export const chatSounds = {
  isMuted(): boolean {
    return muted;
  },

  setMuted(next: boolean): void {
    muted = next;
    if (muted) typingSound.stop();
  },

  startTyping(profile: Profile = 'typing'): void {
    if (muted) return;
    typingSound.start(profile);
  },

  stopTyping(): void {
    typingSound.stop();
  },

  /**
   * Soft single-note "tink" played when the bot finishes a reply.
   * ~880 Hz sine, ~120 ms with exponential decay. Quiet on purpose so
   * it doesn't startle a phone user.
   */
  ding(): void {
    if (muted) return;
    const ctx = getDingCtx();
    if (!ctx) return;
    try {
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.15, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.2);
    } catch {
      // Audio failures are non-critical — never crash the chat panel.
    }
  },
};
